import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AccountMenu, GuestAccountMenu } from './AccountMenu';
import { saveReturnTo } from '../lib/returnTo';
import FindOptionBanner from './FindOptionBanner';
import MobileNavDrawer from './MobileNavDrawer';
import NotificationBell from './NotificationBell';
import CartIcon from './CartIcon';
import ExploreLGRAMenu from './ExploreLGRAMenu';

/**
 * PublicHeader — the single site-wide header used across the entire student &
 * guest experience. Even the "My Account" pages (My Profile, My Bookings,
 * Payment History, Medical Info, etc.) render inside this header so the
 * experience never feels like a separate portal.
 *
 * Nav order (Feb 14 2026 simplification):
 *   • Home removed — the LGRA logo already returns users to the homepage.
 *   • Packages removed — reachable via the Membership + Classes pages.
 *   • "Find My Option" renamed to "Find My Class" and promoted to a purple CTA
 *     button on the right rail (visible on desktop AND mobile) so it stays
 *     easy to spot even after the announcement banner is dismissed.
 *
 * Right rail:
 *   • Find My Class CTA (always shown, primary purple)
 *   • Signed in  → <AccountMenu>
 *   • Signed out → Sign In (saves returnTo) + Join Now
 */
const NAV_ITEMS = [
  { to: '/running-classes', label: 'Classes', testId: 'nav-classes', match: (p) => p.startsWith('/running-classes') || p.startsWith('/all-classes') },
  { to: '/coaching', label: 'Coaching', testId: 'nav-coaching', match: (p) => p.startsWith('/coaching') },
  { to: '/schedule',    label: 'Schedule',   match: (p) => p.startsWith('/schedule') },
  { to: '/race-calendar', label: 'Races', testId: 'nav-upcoming-races', match: (p) => p.startsWith('/race-calendar') || p.startsWith('/find-my-race') },
  { to: '/runner-market', label: 'Market', testId: 'nav-runner-market', match: (p) => p.startsWith('/runner-market') },
  { to: '/coaches',     label: 'Coaches',    match: (p) => p.startsWith('/coaches') },
];

export const PublicHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const findMyClassActive = location.pathname === '/online-training-plan';

  return (
    <>
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200"
      data-testid="public-header"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3.5 gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
          {/* Mobile hamburger — hidden on md+ where the horizontal nav bar
              already covers every section. */}
          <MobileNavDrawer user={user} onLogout={logout} />
          <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0" data-testid="logo-link">
            <img src="/lgra-logo.webp" alt="LGRA Academy – Little Grappy Running Academy" className="h-10 sm:h-11 w-auto" />
            <div className="hidden xl:block leading-tight">
              <div className="text-lg font-black tracking-tight">LGRA Academy</div>
              <div className="text-[10px] text-muted-foreground tracking-wide font-semibold" data-testid="brand-full-name">
                Little Grappy Running Academy
              </div>
            </div>
          </Link>
          {/* Compact "Explore LGRA" dropdown sits next to the logo on desktop.
              On <lg the same grouped list lives inside MobileNavDrawer so
              tablets/phones don't see two menus at once. */}
          <ExploreLGRAMenu />
        </div>

        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium">
          {NAV_ITEMS.map((item) => {
            const active = item.match(location.pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`transition-colors ${active ? 'text-[#7C4DE0] font-bold' : 'text-slate-700 hover:text-[#7C4DE0]'}`}
                data-testid={item.testId || `nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </Link>
            );
          })}
          {user && (
            <Link
              to="/my-races"
              className={`transition-colors ${location.pathname.startsWith('/my-races') ? 'text-[#7C4DE0] font-bold' : 'text-slate-700 hover:text-[#7C4DE0]'}`}
              data-testid="nav-my-races"
            >
              My Races
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {/* Find My Class CTA — always visible, primary purple. Compact icon-
              only variant on <sm so mobile header height never grows. */}
          <Link
            to="/online-training-plan"
            data-testid="nav-find-my-class"
            className={`inline-flex items-center gap-1.5 h-9 sm:h-10 rounded-md text-white text-xs sm:text-[13px] font-black tracking-tight transition-colors shrink-0 px-2 min-[360px]:px-2.5 sm:px-3.5 ${findMyClassActive ? 'bg-[#3F2774]' : 'bg-[#5530B5] hover:bg-[#3F2774]'}`}
            aria-label="Find My Best Training Plan"
            title="Find My Best Training Plan"
          >
            <Sparkles size={14} className="shrink-0" aria-hidden />
            <span className="hidden sm:inline whitespace-nowrap">Find My Plan</span>
          </Link>

          {user ? (
            <>
              <CartIcon />
              <NotificationBell />
              <AccountMenu user={user} onLogout={logout} onNavigate={navigate} />
            </>
          ) : (
            <>
              <CartIcon />
              <GuestAccountMenu
                onSignIn={() => { saveReturnTo(); navigate('/login'); }}
                onJoin={() => { saveReturnTo(); navigate('/register'); }}
              />
            </>
          )}
        </div>
      </div>
    </header>
    <FindOptionBanner />
    </>
  );
};

export default PublicHeader;
